import {
  generateTermPlan as generateTermPlanCallable,
  uploadEHCP as uploadEHCPCallable,
  uploadSupportingReport as uploadSupportingReportCallable,
  extractEHCPSectionE as extractEHCPSectionECallable,
  enqueueDocumentJobV2,
  processDocumentJobV2,
  saveDocumentExtractionCorrectionsV2 as saveDocumentExtractionCorrectionsV2Callable,
  synthesizeStudentProfileV2,
  getStudentEvidenceBundleV2 as getStudentEvidenceBundleV2Callable,
  deleteTherapistReportV2 as deleteTherapistReportV2Callable,
  submitSupportPlanDraftV2 as submitSupportPlanDraftV2Callable,
  reviewSupportPlanV2 as reviewSupportPlanV2Callable,
  getTeacherSupportPlanV2 as getTeacherSupportPlanV2Callable,
  addProfessionalRecommendationToWaitlist as addProfessionalRecommendationToWaitlistCallable,
  dismissProfessionalRecommendation as dismissProfessionalRecommendationCallable,
  createApdrEvidenceSnapshot as createApdrEvidenceSnapshotCallable,
  listApdrEvidenceSnapshots as listApdrEvidenceSnapshotsCallable,
  getApdrTargetReviewQueueForStudent as getApdrTargetReviewQueueForStudentCallable,
  approveApdrTargetReviewQueueItem as approveApdrTargetReviewQueueItemCallable,
  generateReviewPackV2 as generateReviewPackV2Callable,
  listReviewPacksForStudentV2 as listReviewPacksForStudentV2Callable,
  getStudentAuditTrail as getStudentAuditTrailCallable,
} from './functions';
import { ref, uploadBytes } from 'firebase/storage';
import { storage } from '../firebase';

export async function generateTermPlan(studentId, day) {
  if (!studentId) throw new Error('studentId is required');
  const response = await generateTermPlanCallable({
    studentId,
    ...(day ? { day } : {}),
  });
  return response?.data || {};
}

export async function uploadEHCP(studentId, file) {
  if (!studentId) throw new Error('studentId is required');
  if (!file) throw new Error('A PDF file is required');

  const uploadRes = await uploadEHCPCallable({
    studentId,
    filename: file.name,
  });

  const payload = uploadRes?.data || {};
  const uploadUrl = payload.uploadUrl;
  if (uploadUrl) {
    await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': file.type || 'application/pdf',
      },
      body: file,
    });
  } else if (payload.storagePath) {
    // Emulator-safe fallback when signed URL generation is unavailable.
    const objectRef = ref(storage, payload.storagePath);
    await uploadBytes(objectRef, file, {
      contentType: file.type || 'application/pdf',
    });
  } else {
    throw new Error('Upload endpoint did not provide an upload URL or storage path');
  }

  return payload;
}

export async function extractEHCPSectionE(docId) {
  if (!docId) throw new Error('docId is required');
  const extracted = await extractEHCPSectionECallable({ docId });
  return extracted?.data || {};
}

export async function uploadSupportingReport(studentId, reportType, file) {
  if (!studentId) throw new Error('studentId is required');
  if (!reportType) throw new Error('reportType is required');
  if (!file) throw new Error('A PDF file is required');

  const uploadRes = await uploadSupportingReportCallable({
    studentId,
    filename: file.name,
    reportType,
  });

  const payload = uploadRes?.data || {};
  const uploadUrl = payload.uploadUrl;
  if (uploadUrl) {
    await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': file.type || 'application/pdf' },
      body: file,
    });
  } else if (payload.storagePath) {
    const objectRef = ref(storage, payload.storagePath);
    await uploadBytes(objectRef, file, { contentType: file.type || 'application/pdf' });
  } else {
    throw new Error('Upload endpoint did not provide an upload URL or storage path');
  }

  return payload;
}

export async function runEhcpV2Synthesis({
  studentId,
  storagePath,
  fileSizeBytes,
  mimeType = 'application/pdf',
}) {
  if (!studentId) throw new Error('studentId is required');
  if (!storagePath) throw new Error('storagePath is required');

  const queued = await enqueueDocumentJobV2({
    studentId,
    filePath: storagePath,
    fileSizeBytes,
    mimeType,
    jobType: 'extract_ehcp',
  });

  const job = queued?.data?.job;
  if (!job?.jobId) throw new Error('Failed to enqueue document processing job');

  const processed = await processDocumentJobV2({ jobId: job.jobId });
  const extraction = processed?.data?.extraction || {};

  const synthesized = await synthesizeStudentProfileV2({
    studentId,
    documentExtraction: {
      sourcePath: storagePath,
      sectionE: extraction.sectionE || {},
      sectionF: extraction.sectionF || {},
    },
  });

  return {
    job,
    extraction,
    profile: synthesized?.data?.profile || null,
    onePageProfile: synthesized?.data?.onePageProfile || null,
  };
}

export async function runTherapistReportV2Synthesis({
  studentId,
  storagePath,
  fileSizeBytes,
  mimeType = 'application/pdf',
  typedObservation,
}) {
  if (!studentId) throw new Error('studentId is required');
  if (!storagePath) throw new Error('storagePath is required');

  const queued = await enqueueDocumentJobV2({
    studentId,
    filePath: storagePath,
    fileSizeBytes,
    mimeType,
    jobType: 'extract_therapist_report',
  });

  const job = queued?.data?.job;
  if (!job?.jobId) throw new Error('Failed to enqueue therapist report processing job');

  const processed = await processDocumentJobV2({ jobId: job.jobId });
  const extraction = processed?.data?.extraction || {};
  const synthesisObservation = typedObservation || extraction?.therapistReport?.summary || '';

  const synthesized = await synthesizeStudentProfileV2({
    studentId,
    typedObservation: synthesisObservation,
  });

  return {
    job,
    extraction,
    profile: synthesized?.data?.profile || null,
    onePageProfile: synthesized?.data?.onePageProfile || null,
  };
}

export async function saveDocumentExtractionCorrectionsV2({
  jobId,
  studentId,
  sourcePath,
  sectionE,
  sectionF,
}) {
  if (!jobId) throw new Error('jobId is required');
  if (!studentId) throw new Error('studentId is required');
  if (!sectionE || typeof sectionE !== 'object') throw new Error('sectionE is required');

  const saved = await saveDocumentExtractionCorrectionsV2Callable({
    jobId,
    studentId,
    sourcePath,
    sectionE,
    sectionF: sectionF || {},
  });

  const payload = saved?.data || {};
  const synthesized = await synthesizeStudentProfileV2({
    studentId,
    documentExtraction: {
      sourcePath: payload?.sourcePath || sourcePath || '',
      sectionE,
      sectionF: sectionF || {},
    },
  });

  return {
    saveResult: payload,
    profile: synthesized?.data?.profile || null,
    onePageProfile: synthesized?.data?.onePageProfile || null,
  };
}

export async function createApdrEvidenceSnapshot({ studentId, notes, source }) {
  const response = await createApdrEvidenceSnapshotCallable({ studentId, notes, source });
  return response?.data || {};
}

export async function listApdrEvidenceSnapshots({ studentId, limit = 10 }) {
  const response = await listApdrEvidenceSnapshotsCallable({ studentId, limit });
  return response?.data || {};
}

export async function getApdrTargetReviewQueueForStudent({ studentId }) {
  const response = await getApdrTargetReviewQueueForStudentCallable({ studentId });
  return response?.data || {};
}

export async function approveApdrTargetReviewQueueItem({
  queueId,
  approved,
  reviewerNotes,
  academicYear,
  halfTerm,
}) {
  const payload = {
    queueId,
    approved,
    ...(reviewerNotes ? { reviewerNotes } : {}),
    ...(academicYear ? { academicYear } : {}),
    ...(halfTerm ? { halfTerm } : {}),
  };
  const response = await approveApdrTargetReviewQueueItemCallable(payload);
  return response?.data || {};
}

export async function generateReviewPackV2({ studentId, audience = 'parent' }) {
  const response = await generateReviewPackV2Callable({ studentId, audience });
  return response?.data || {};
}

export async function listReviewPacksForStudentV2({ studentId, limit = 10 }) {
  const response = await listReviewPacksForStudentV2Callable({ studentId, limit });
  return response?.data || {};
}

export async function getStudentAuditTrail({ studentId, limit = 100 }) {
  const response = await getStudentAuditTrailCallable({ studentId, limit });
  return response?.data || {};
}

export async function getStudentEvidenceBundleV2({ studentId }) {
  const response = await getStudentEvidenceBundleV2Callable({ studentId });
  return response?.data || {};
}

export async function deleteTherapistReportV2({ jobId, studentId }) {
  if (!jobId) throw new Error('jobId is required');
  if (!studentId) throw new Error('studentId is required');
  const response = await deleteTherapistReportV2Callable({ jobId, studentId });
  return response?.data || {};
}

export async function submitSupportPlanDraftV2({ studentId, sections, changeSummary }) {
  const response = await submitSupportPlanDraftV2Callable({ studentId, sections, changeSummary });
  return response?.data || {};
}

export async function reviewSupportPlanV2({ studentId, action, notes }) {
  const response = await reviewSupportPlanV2Callable({
    studentId,
    action,
    notes: typeof notes === 'string' ? notes : '',
  });
  return response?.data || {};
}

export async function getTeacherSupportPlanV2({ studentId }) {
  const response = await getTeacherSupportPlanV2Callable({ studentId });
  return response?.data || {};
}

export async function addProfessionalRecommendationToWaitlist({ studentId, recommendationKey }) {
  const response = await addProfessionalRecommendationToWaitlistCallable({ studentId, recommendationKey });
  return response?.data || {};
}

export async function dismissProfessionalRecommendation({ studentId, recommendationKey }) {
  const response = await dismissProfessionalRecommendationCallable({ studentId, recommendationKey });
  return response?.data || {};
}
